import React from 'react';
import { ArrowLeft, Shield, Award, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import WhatsAppButton from '../../components/WhatsAppButton';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../i18n/pageTranslations';

const TecnicaMateriales = () => {
  const navigate = useNavigate();
  const lang = useLanguage();

  const texts = {
    es: {
      backTo: 'Volver a ¿Qué es Rinomodelación?',
      title: 'Técnica y Materiales',
      subtitle: 'de Bioplastia y Rinomodelación',
      intro: 'El Dr. Ángel Ruiz Norro utiliza exclusivamente materiales de la más alta calidad, aprobados por las autoridades sanitarias europeas y con un extenso historial de seguridad comprobada.',
      hyaluronicTitle: 'Ácido Hialurónico',
      hyaluronicDesc: 'Para resultados <strong>temporales</strong> (12-18 meses). Producto biocompatible y reversible, ideal para pacientes que desean probar el resultado antes de optar por una solución permanente.',
      hyaluronicBenefits: ['100% biocompatible', 'Reversible con hialuronidasa', 'Resultados naturales'],
      hydroxyTitle: 'Hidroxiapatita Cálcica',
      hydroxyDesc: 'Para resultados <strong>permanentes</strong>. Material bioestimulador que induce la formación de colágeno propio, proporcionando resultados estables y duraderos en el tiempo.',
      hydroxyBenefits: ['Bioestimulador natural', 'Resultados permanentes', 'Integración tisular completa'],
      techniqueTitle: 'La Técnica del Dr. Norro',
      steps: [
        { title: 'Análisis Personalizado', desc: 'Estudio detallado de tu rostro y nariz para diseñar el tratamiento ideal.' },
        { title: 'Aplicación Precisa', desc: 'Técnica microcánula que minimiza hematomas y maximiza la precisión.' },
        { title: 'Resultado Natural', desc: 'Modelado artístico respetando la armonía facial de cada paciente.' },
      ],
      safetyTitle: 'Seguridad y Garantías',
      safetyItems: [
        { title: 'Productos Certificados CE', desc: 'Todos los materiales cuentan con certificación europea' },
        { title: 'Técnica Depurada', desc: 'Más de 25 años de experiencia y miles de procedimientos' },
        { title: 'Seguimiento Personalizado', desc: 'Control post-tratamiento incluido sin coste adicional' },
        { title: 'Instalaciones Autorizadas', desc: 'Clínicas con todas las licencias sanitarias en regla' },
      ],
      cta: 'CONSULTA SIN COMPROMISO',
    },
    en: {
      backTo: 'Back to What is Rhinomodeling?',
      title: 'Technique and Materials',
      subtitle: 'for Bioplasty and Rhinomodeling',
      intro: 'Dr. Ángel Ruiz Norro exclusively uses the highest quality materials, approved by European health authorities and with an extensive proven safety record.',
      hyaluronicTitle: 'Hyaluronic Acid',
      hyaluronicDesc: 'For <strong>temporary</strong> results (12-18 months). Biocompatible and reversible product, ideal for patients who want to try the result before opting for a permanent solution.',
      hyaluronicBenefits: ['100% biocompatible', 'Reversible with hyaluronidase', 'Natural results'],
      hydroxyTitle: 'Calcium Hydroxyapatite',
      hydroxyDesc: 'For <strong>permanent</strong> results. Biostimulating material that induces the formation of your own collagen, providing stable and lasting results over time.',
      hydroxyBenefits: ['Natural biostimulator', 'Permanent results', 'Complete tissue integration'],
      techniqueTitle: 'Dr. Norro\'s Technique',
      steps: [
        { title: 'Personalized Analysis', desc: 'Detailed study of your face and nose to design the ideal treatment.' },
        { title: 'Precise Application', desc: 'Microcannula technique that minimizes bruising and maximizes precision.' },
        { title: 'Natural Result', desc: 'Artistic modeling respecting the facial harmony of each patient.' },
      ],
      safetyTitle: 'Safety and Guarantees',
      safetyItems: [
        { title: 'CE Certified Products', desc: 'All materials have European certification' },
        { title: 'Refined Technique', desc: 'More than 25 years of experience and thousands of procedures' },
        { title: 'Personalized Follow-up', desc: 'Post-treatment control included at no additional cost' },
        { title: 'Authorized Facilities', desc: 'Clinics with all health licenses in order' },
      ],
      cta: 'FREE CONSULTATION',
    },
    fr: {
      backTo: 'Retour à Qu\'est-ce que le Rhinomodelage?',
      title: 'Technique et Matériaux',
      subtitle: 'de Bioplastie et Rhinomodelage',
      intro: 'Le Dr. Ángel Ruiz Norro utilise exclusivement des matériaux de la plus haute qualité, approuvés par les autorités sanitaires européennes et avec un long historique de sécurité prouvée.',
      hyaluronicTitle: 'Acide Hyaluronique',
      hyaluronicDesc: 'Pour des résultats <strong>temporaires</strong> (12-18 mois). Produit biocompatible et réversible, idéal pour les patients qui souhaitent essayer le résultat avant d\'opter pour une solution permanente.',
      hyaluronicBenefits: ['100% biocompatible', 'Réversible avec la hyaluronidase', 'Résultats naturels'],
      hydroxyTitle: 'Hydroxyapatite de Calcium',
      hydroxyDesc: 'Pour des résultats <strong>permanents</strong>. Matériau biostimulant qui induit la formation de votre propre collagène, fournissant des résultats stables et durables dans le temps.',
      hydroxyBenefits: ['Biostimulateur naturel', 'Résultats permanents', 'Intégration tissulaire complète'],
      techniqueTitle: 'La Technique du Dr. Norro',
      steps: [
        { title: 'Analyse Personnalisée', desc: 'Étude détaillée de votre visage et nez pour concevoir le traitement idéal.' },
        { title: 'Application Précise', desc: 'Technique microcanule qui minimise les hématomes et maximise la précision.' },
        { title: 'Résultat Naturel', desc: 'Modelage artistique respectant l\'harmonie faciale de chaque patient.' },
      ],
      safetyTitle: 'Sécurité et Garanties',
      safetyItems: [
        { title: 'Produits Certifiés CE', desc: 'Tous les matériaux ont la certification européenne' },
        { title: 'Technique Affinée', desc: 'Plus de 25 ans d\'expérience et des milliers de procédures' },
        { title: 'Suivi Personnalisé', desc: 'Contrôle post-traitement inclus sans coût supplémentaire' },
        { title: 'Installations Autorisées', desc: 'Cliniques avec toutes les licences sanitaires en règle' },
      ],
      cta: 'CONSULTATION SANS ENGAGEMENT',
    },
    pt: {
      backTo: 'Voltar para O que é Rinomodelação?',
      title: 'Técnica e Materiais',
      subtitle: 'de Bioplastia e Rinomodelação',
      intro: 'O Dr. Ángel Ruiz Norro utiliza exclusivamente materiais da mais alta qualidade, aprovados pelas autoridades sanitárias europeias e com um extenso histórico de segurança comprovada.',
      hyaluronicTitle: 'Ácido Hialurônico',
      hyaluronicDesc: 'Para resultados <strong>temporários</strong> (12-18 meses). Produto biocompatível e reversível, ideal para pacientes que desejam experimentar o resultado antes de optar por uma solução permanente.',
      hyaluronicBenefits: ['100% biocompatível', 'Reversível com hialuronidase', 'Resultados naturais'],
      hydroxyTitle: 'Hidroxiapatita de Cálcio',
      hydroxyDesc: 'Para resultados <strong>permanentes</strong>. Material bioestimulador que induz a formação do seu próprio colágeno, proporcionando resultados estáveis e duradouros ao longo do tempo.',
      hydroxyBenefits: ['Bioestimulador natural', 'Resultados permanentes', 'Integração tecidual completa'],
      techniqueTitle: 'A Técnica do Dr. Norro',
      steps: [
        { title: 'Análise Personalizada', desc: 'Estudo detalhado do seu rosto e nariz para projetar o tratamento ideal.' },
        { title: 'Aplicação Precisa', desc: 'Técnica de microcânula que minimiza hematomas e maximiza a precisão.' },
        { title: 'Resultado Natural', desc: 'Modelagem artística respeitando a harmonia facial de cada paciente.' },
      ],
      safetyTitle: 'Segurança e Garantias',
      safetyItems: [
        { title: 'Produtos Certificados CE', desc: 'Todos os materiais têm certificação europeia' },
        { title: 'Técnica Refinada', desc: 'Mais de 25 anos de experiência e milhares de procedimentos' },
        { title: 'Acompanhamento Personalizado', desc: 'Controle pós-tratamento incluído sem custo adicional' },
        { title: 'Instalações Autorizadas', desc: 'Clínicas com todas as licenças sanitárias em dia' },
      ],
      cta: 'CONSULTA SEM COMPROMISSO',
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-cream">
        <div className="container mx-auto px-4">
          <button 
            onClick={() => navigate('/rinomodelacion/que-es')}
            className="flex items-center gap-2 text-gold hover:text-gold transition-colors"
          >
            <ArrowLeft size={20} />
            <span>{t.backTo}</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-b from-cream to-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h1 className="text-4xl md:text-5xl font-light text-[#1C1C1A] mb-6">
            {t.title}
          </h1>
          <p className="text-xl text-text-secondary">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          
          {/* Introducción */}
          <div className="prose max-w-none mb-12">
            <p className="text-lg text-text-secondary leading-relaxed mb-6">
              {t.intro}
            </p>
          </div>

          {/* Materiales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-ivory p-8 rounded-none shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gold/10 rounded-none flex items-center justify-center">
                  <Shield className="text-gold" size={32} />
                </div>
                <h3 className="text-2xl font-light text-[#1C1C1A]">{t.hyaluronicTitle}</h3>
              </div>
              <p className="text-text-secondary mb-4" dangerouslySetInnerHTML={{ __html: t.hyaluronicDesc }} />
              <ul className="space-y-2">
                {t.hyaluronicBenefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-2 text-text-secondary">
                    <CheckCircle className="text-gold" size={18} />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-ivory p-8 rounded-none shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gold/10 rounded-none flex items-center justify-center">
                  <Award className="text-gold" size={32} />
                </div>
                <h3 className="text-2xl font-light text-[#1C1C1A]">{t.hydroxyTitle}</h3>
              </div>
              <p className="text-text-secondary mb-4" dangerouslySetInnerHTML={{ __html: t.hydroxyDesc }} />
              <ul className="space-y-2">
                {t.hydroxyBenefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-2 text-text-secondary">
                    <CheckCircle className="text-blue-500" size={18} />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Técnica */}
          <div className="bg-gradient-to-r from-[#1C1C1A] to-[#1C1C1A] p-8 rounded-none text-white mb-12">
            <h2 className="text-3xl font-light mb-8 text-center">{t.techniqueTitle}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {t.steps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-ivory/20 rounded-none flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold">{index + 1}</span>
                  </div>
                  <h4 className="text-xl font-semibold mb-2">{step.title}</h4>
                  <p className="text-white/80">
                    {step.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Seguridad */}
          <div className="bg-cream p-8 rounded-none">
            <h3 className="text-2xl font-light text-[#1C1C1A] mb-6 text-center">{t.safetyTitle}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {t.safetyItems.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="text-gold mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="font-semibold text-[#1C1C1A]">{item.title}</h4>
                    <p className="text-text-secondary">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Button 
              onClick={() => navigate('/rinomodelacion/contacto')}
              className="bg-gold hover:bg-gold text-white px-8 py-6 text-lg rounded-none"
            >
              {t.cta}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default TecnicaMateriales;
